import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { HelpPopover, Stack, buttonVariantClasses, cx } from "@wato/ui";
import { visibilityFor } from "@wato/business-types";
import { PageError, PageLoading } from "../../components/common";
import { useApi } from "../../api/context";
import type { ChecklistStepKey, ChecklistView } from "../../api/onboarding-types";
import { TryDemoPanel } from "../onboarding/TryDemoPanel";
import { mayUse } from "../roles/role-label";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { useFeaturesWithData } from "../workspace/useFeaturesWithData";
import { useShowAll } from "../workspace/show-all";

/** One thing the member can do next. `to` is relative to `/w/:workspaceId/`. */
export interface NextStep {
  readonly id: ChecklistStepKey | "inbox";
  readonly to: string;
  readonly done: boolean;
  readonly titleKey: MessageKey;
  readonly bodyKey: MessageKey;
  readonly ctaKey: MessageKey;
}

const STEP_META: Record<ChecklistStepKey, { to: string; permission: string }> = {
  channelConnected: { to: "channels", permission: "channels.view" },
  flowPublished: { to: "flows", permission: "flows.view" },
  templateAdded: { to: "templates", permission: "templates.view" },
  teammateInvited: { to: "settings/team", permission: "team.view" },
};

const key = (id: ChecklistStepKey, part: "title" | "body" | "cta") =>
  `setup.checklist.steps.${id}.${part}` as MessageKey;

/**
 * UXR-20: the ordered steps this member can act on. Steps the role cannot open or the business type
 * hides are left out; the inbox is the next step for a member with nothing to set up (e.g. an Agent).
 * The "done" state is always the server's live checklist (never marked done by the UI).
 */
export function buildNextSteps(input: {
  readonly view: ChecklistView;
  readonly canUse: (permission: string) => boolean;
  readonly isVisible: (to: string) => boolean;
}): NextStep[] {
  const steps: NextStep[] = input.view.steps
    .filter(
      (s) => input.canUse(STEP_META[s.key].permission) && input.isVisible(STEP_META[s.key].to),
    )
    .map((s) => ({
      id: s.key,
      to: STEP_META[s.key].to,
      done: s.done,
      titleKey: key(s.key, "title"),
      bodyKey: key(s.key, "body"),
      ctaKey: key(s.key, "cta"),
    }));
  if (steps.length === 0 && input.canUse("inbox.view")) {
    return [
      {
        id: "inbox",
        to: "conversations",
        done: false,
        titleKey: "dashboard.home.nextStep.inbox.title",
        bodyKey: "dashboard.home.nextStep.inbox.body",
        ctaKey: "dashboard.home.nextStep.inbox.cta",
      },
    ];
  }
  return steps;
}

/**
 * UXR-20: ONE next-step card (title, one sentence, one primary button) plus a collapsed list of the
 * remaining steps with progress. Complete = a single congratulation line, no card.
 */
export function NextStepCard({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const hasData = useFeaturesWithData(workspaceId);
  const showAllType = useShowAll(workspace).businessType;
  const [view, setView] = useState<ChecklistView | null>(null);
  const [failed, setFailed] = useState<unknown>(null);
  const load = useCallback(() => {
    setFailed(null);
    api.getOnboardingChecklist(workspaceId).then(setView, (e: unknown) => setFailed(e ?? true));
  }, [api, workspaceId]);
  useEffect(load, [load]);
  const [hasCloud, setHasCloud] = useState(false);
  useEffect(() => {
    api.listChannels(workspaceId).then(
      (all) => setHasCloud(all.some((c) => c.provider === "whatsapp_cloud")),
      () => setHasCloud(false),
    );
  }, [api, workspaceId]);
  if (failed && !view)
    return (
      <PageError
        message={t("setup.checklist.loadError")}
        error={failed === true ? undefined : failed}
        workspaceId={workspaceId}
        onRetry={load}
      />
    );
  if (!view) return <PageLoading />;

  const steps = buildNextSteps({
    view,
    canUse: (p) => mayUse(workspace, p),
    isVisible: (to) => visibilityFor(showAllType, to, { hasData }) !== "hide",
  });
  if (steps.length === 0) return null;
  const todo = steps.filter((s) => !s.done);
  const next = todo[0];
  if (!next) {
    return (
      <p role="status" data-testid="next-step-complete" className="text-sm text-fg">
        {t("dashboard.home.nextStep.complete")}
      </p>
    );
  }
  const done = steps.length - todo.length;
  const rest = steps.filter((s) => s !== next);
  const noChannel = view.steps.some((s) => s.key === "channelConnected" && !s.done);
  return (
    <Stack gap={2} data-testid="next-step">
      <section
        aria-label={t("dashboard.home.nextStep.heading")}
        className="rounded-2xl border border-app-line bg-app-surface p-5"
      >
        <Stack gap={2}>
          <p className="text-xs font-medium text-fg-muted">
            {t("dashboard.home.nextStep.heading")}
          </p>
          <div className="flex items-center gap-1">
            <h3 className="text-lg font-semibold text-fg" data-testid="next-step-title">
              {t(next.titleKey)}
            </h3>
            <HelpPopover testId="next-step-help">
              <p className="m-0">{t(next.bodyKey)}</p>
            </HelpPopover>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/w/${workspaceId}/${next.to}`}
              data-testid="next-step-cta"
              className={cx(
                "inline-flex h-10 items-center rounded-md px-4 text-sm font-medium",
                buttonVariantClasses.primary,
              )}
            >
              {t(next.ctaKey)}
            </Link>
          </div>
          {hasCloud && mayUse(workspace, "flows.view") ? (
            <p className="text-sm text-fg-muted" data-testid="next-step-build-flow">
              {t("dashboard.home.nextStep.buildFlow.body")}{" "}
              <Link
                to={`/w/${workspaceId}/flows`}
                className="font-medium text-on-action-ghost underline"
              >
                {t("dashboard.home.nextStep.buildFlow.cta")}
              </Link>
            </p>
          ) : null}
          {noChannel && next.id !== "inbox" ? <TryDemoPanel workspaceId={workspaceId} /> : null}
        </Stack>
      </section>
      {rest.length > 0 ? (
        <details
          className="rounded-2xl border border-app-line bg-app-surface p-4"
          data-testid="next-step-rest"
        >
          <summary className="cursor-pointer text-sm font-medium text-fg">
            {t("dashboard.home.nextStep.remaining")}
            <span className="ms-2 text-fg-muted">
              {t("dashboard.home.nextStep.progress", { done, total: steps.length })}
            </span>
          </summary>
          <ol className="mt-3 flex flex-col gap-2">
            {rest.map((s) => (
              <li
                key={s.id}
                data-step={s.id}
                data-done={s.done ? "true" : "false"}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span className="text-fg">
                  <span aria-hidden="true">{s.done ? "✓ " : "○ "}</span>
                  {t(s.titleKey)}
                </span>
                {s.done ? null : (
                  <Link to={`/w/${workspaceId}/${s.to}`} className="text-on-action-ghost underline">
                    {t(s.ctaKey)}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </Stack>
  );
}
